import React, { useCallback, useEffect, useRef } from 'react';
import {
  convertPlainTextToHtml,
  ensureRichText,
  normalizeRichTextInput,
} from '../utils/richText';
import './RichTextEditor.css';

const TOOLBAR_ACTIONS = [
  { command: 'bold', label: 'Negritas', icon: 'B' },
  { command: 'italic', label: 'Italicas', icon: 'I' },
  { command: 'underline', label: 'Subrayado', icon: 'U' },
  { command: 'insertOrderedList', label: 'Lista numerada', icon: '1.' },
  { command: 'insertUnorderedList', label: 'Lista con vinetas', icon: '*' },
  { command: 'formatBlock', value: 'blockquote', label: 'Cita', icon: '""' },
];

const execCommand = (command, value) => {
  if (typeof document === 'undefined' || typeof document.execCommand !== 'function') {
    return;
  }
  document.execCommand(command, false, value);
};

const RichTextEditor = ({
  id,
  value = '',
  onChange,
  placeholder = '',
  ariaLabelledby,
  ariaDescribedby,
  className = '',
}) => {
  const editorRef = useRef(null);
  const latestValueRef = useRef('');

  const sanitizedValue = ensureRichText(value);

  useEffect(() => {
    const editor = editorRef.current;
    if (!editor) return;
    if (sanitizedValue !== latestValueRef.current) {
      latestValueRef.current = sanitizedValue;
      editor.innerHTML = sanitizedValue;
    }
  }, [sanitizedValue]);

  const emitChange = useCallback(() => {
    const editor = editorRef.current;
    if (!editor) return;
    const nextValue = normalizeRichTextInput(editor.innerHTML);
    latestValueRef.current = nextValue;
    if (onChange) {
      onChange(nextValue);
    }
  }, [onChange]);

  const handleInput = useCallback(() => {
    emitChange();
  }, [emitChange]);

  const handlePaste = useCallback(
    event => {
      if (!editorRef.current) return;
      const clipboardData = event.clipboardData || window.clipboardData;
      if (!clipboardData) return;
      event.preventDefault();
      const text = clipboardData.getData('text/plain');
      if (!text) return;
      const fragment = normalizeRichTextInput(convertPlainTextToHtml(text));
      execCommand('insertHTML', fragment);
      emitChange();
    },
    [emitChange]
  );

  const handleToolbarClick = useCallback(
    (command, value) => {
      const editor = editorRef.current;
      if (!editor) return;
      editor.focus();
      if (command === 'formatBlock' && value) {
        execCommand(command, value);
      } else {
        execCommand(command);
      }
      emitChange();
    },
    [emitChange]
  );

  return (
    <div className="rich-text-editor-wrapper">
      <div className="rich-text-editor__toolbar" role="toolbar" aria-label="Formato de texto">
        {TOOLBAR_ACTIONS.map(action => (
          <button
            key={action.command + (action.value || '')}
            type="button"
            className="rich-text-editor__button"
            title={action.label}
            onMouseDown={event => event.preventDefault()}
            onClick={() => handleToolbarClick(action.command, action.value)}
          >
            {action.icon}
          </button>
        ))}
      </div>
      <div
        id={id}
        ref={editorRef}
        className={`rich-text-editor${className ? ` ${className}` : ''}`}
        contentEditable
        role="textbox"
        aria-multiline="true"
        aria-labelledby={ariaLabelledby}
        aria-describedby={ariaDescribedby}
        data-placeholder={placeholder || undefined}
        onInput={handleInput}
        onBlur={handleInput}
        onPaste={handlePaste}
        suppressContentEditableWarning
      />
    </div>
  );
};

export default RichTextEditor;
